import { z } from "zod";
import { PAGE_SIZE_MAX, paginationQuery, searchParam } from "../pagination";
import type { NocRef } from "./common";
import type { MeetEventSummary, MeetSportSummary } from "./meets";
import type { PersonGender } from "./participants";
import type { OfficialRole } from "./vocab";

/**
 * Officials — the register of technical officials of one games (OVR's
 * Participants › Officials; the old OVR's "Judges"):
 *
 *   GET /officials?games_id=…              Paginated<OfficialItem>
 *   GET /officials/:acCardId?games_id=…    OfficialDetail
 *
 * A technical official is an APPROVED card of the accreditation category
 * `TECHNICAL_OFFICIAL_CATEGORY_CODE` ("TO", case-blind) in the games — what
 * the old OVR found by the "Pegawai Teknikal" sub-function. Their sports are
 * the card's (`sport_id`, `sport_ids`, or every sport). Their assignments are
 * the `meet_officials` rows naming the person in a meet of the games — the
 * seat a meet screen gives them; an official typed by name only (no person)
 * is on the meet, not in the register.
 *
 * Read-only, `COMPETITION_WRITE_ROLES`, and — unlike the competition reads —
 * narrowed by the caller's scope (`cardInScope`): this lists CARDS.
 */

export const TECHNICAL_OFFICIAL_CATEGORY_CODE = "TO";
export const OFFICIALS_PAGE_SIZE_DEFAULT = 50;
/** The most assignments one official's detail lists; `assignments_count` is always the true number. */
export const OFFICIAL_ASSIGNMENTS_MAX = 200;

const idField = z.string().uuid().toLowerCase();

/** `GET /officials` — by printed name. `search` matches the name and the card number. */
export const listOfficialsQuery = z.object({
	games_id: idField,
	search: searchParam.optional(),
	responsible_organization_id: idField.optional(),
	sport_id: idField.optional(),
	page: paginationQuery.shape.page,
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(OFFICIALS_PAGE_SIZE_DEFAULT),
});
export type ListOfficialsQuery = z.input<typeof listOfficialsQuery>;

export const officialDetailQuery = z.object({ games_id: idField });
export type OfficialDetailQuery = z.input<typeof officialDetailQuery>;

export interface OfficialItem {
	ac_card_id: string;
	people_id: string;
	name: string;
	gender: PersonGender | null;
	/** Set once the card is approved (always, here); print with `formatAccreditationNo`. */
	accreditation_no: number | null;
	organization: NocRef | null;
	/** The card's function — "Technical Delegate", "Judge / Referee". */
	function_name: string | null;
	all_sports: boolean;
	sports: MeetSportSummary[];
	/** Seats on meets of the games. */
	meets_count: number;
}

export interface OfficialAssignment {
	meet_official_id: string;
	meet_id: string;
	meet_name: string;
	scheduled_at: string | null;
	sport: MeetSportSummary;
	event: MeetEventSummary;
	role: OfficialRole;
	position: string | null;
}

export interface OfficialDetail extends OfficialItem {
	/** Soonest first; at most `OFFICIAL_ASSIGNMENTS_MAX`. */
	assignments: OfficialAssignment[];
	assignments_count: number;
}
